import {
    type EditablePurchaseOrderItemForm,
    ItemGrid,
    ensureTrailingRow,
    itemsFromPO,
    serializeItems,
} from '@/components/features/PurchaseOrders/ItemGrid';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/shadcn/drawer';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useIsTabletOrMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import { cn, formatDate, formatDateTime } from '@/lib/utils';
import { type BreadcrumbItem, type PageProps } from '@/types';
import type { PurchaseOrder, PurchaseOrderItemCategory, PurchaseOrderItemForm, PurchaseOrderStatus } from '@/types/purchase-order';
import { Head, Link, router, usePage } from '@inertiajs/react';
import {
    Activity,
    AlertCircle,
    ArrowLeft,
    Building2,
    CalendarDays,
    CheckCircle2,
    Circle,
    ClipboardCheck,
    Copy,
    FileSpreadsheet,
    FolderOpen,
    Handshake,
    Info,
    Link2,
    MessageSquare,
    MoreHorizontal,
    Pencil,
    Receipt,
    Trash2,
    Warehouse,
    XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface ProjectOption {
    id: string;
    uuid: string;
    contract_name: string;
    contract_number: string;
}

interface LeadOption {
    id: string;
    uuid: string;
    lead_number: string | null;
}

interface POShowProps extends PageProps {
    [key: string]: unknown;
    purchaseOrder: PurchaseOrder;
    isOverdue: boolean;
    categories: PurchaseOrderItemCategory[];
    projects: ProjectOption[];
    leads: LeadOption[];
}

const STATUS_COLORS: Record<PurchaseOrderStatus, string> = {
    draft: 'bg-muted text-muted-foreground border-border',
    ordered: 'bg-info-subtle text-info-subtle-foreground border-border',
    partially_received: 'bg-warning-subtle text-warning-subtle-foreground border-border',
    received: 'bg-success-subtle text-success-subtle-foreground border-border',
    cancelled: 'bg-danger-subtle text-danger-subtle-foreground border-border',
};

function StatusBadge({ status }: { status: PurchaseOrderStatus }) {
    const { t } = useTranslations();
    return (
        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${STATUS_COLORS[status]}`}>
            {t(`purchase_orders.statuses.${status}`, status.replace(/_/g, ' '))}
        </span>
    );
}

function formatCurrency(amount: number | string | null | undefined, currency: string): string {
    const num = Number(amount ?? 0);
    return new Intl.NumberFormat('lt-LT', { style: 'currency', currency: currency || 'EUR' }).format(num);
}

function safeFormatDate(date: string | null | undefined): string {
    if (!date) return '—';
    return formatDate(date ?? '');
}

function safeFormatDateTime(date: string | null | undefined): string {
    if (!date) return '—';
    return formatDateTime(date ?? '');
}

function calcLineTotal(item: PurchaseOrderItemForm): number {
    return (parseFloat(item.qty_ordered) || 0) * (parseFloat(item.unit_price) || 0);
}

function calcSubtotal(items: PurchaseOrderItemForm[]): number {
    return items.reduce((sum, i) => sum + calcLineTotal(i), 0);
}

// ─── Record Receipt sheet ─────────────────────────────────────────────────────

interface ReceiptDialogProps {
    open: boolean;
    onClose: () => void;
    po: PurchaseOrder;
    t: (key: string, fallback?: string) => string;
}

function ReceiptDialog({ open, onClose, po, t }: ReceiptDialogProps) {
    const [receivedAt, setReceivedAt] = useState(new Date().toISOString().slice(0, 16));
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [qtys, setQtys] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);

    const openItems = po.items.filter((i) => (i.qty_received ?? 0) < Number(i.qty_ordered));

    const handleSubmit = () => {
        const items = openItems.map((i) => ({ item_id: i.id, qty_received: parseFloat(qtys[i.id] ?? '0') || 0 })).filter((i) => i.qty_received > 0);

        if (items.length === 0) {
            toast.error(t('toasts.purchase_orders_show.receipt_enter_qty', 'Enter at least one received quantity.'));
            return;
        }

        setSubmitting(true);
        router.post(route('purchase-orders.receipts.store', po.id), { received_at: receivedAt, reference, notes, items } as never, {
            onSuccess: () => {
                onClose();
                toast.success(t('purchase_orders.messages.receipt_recorded', 'Receipt recorded.'));
            },
            onError: () => toast.error(t('toasts.purchase_orders_show.receipt_failed', 'Failed to record receipt.')),
            onFinish: () => setSubmitting(false),
        });
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onClose} variant="sheet">
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('purchase_orders.receipt.dialog_title', 'Record Receipt')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('purchase_orders.receipt.dialog_desc', 'Enter quantities received in this delivery.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>
                <ResponsiveModalBody className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>{t('purchase_orders.receipt.received_at', 'Received At')}</Label>
                            <Input type="datetime-local" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} className="mt-1" />
                        </div>
                        <div>
                            <Label>{t('purchase_orders.receipt.reference', 'Reference / Delivery Note #')}</Label>
                            <Input
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                placeholder={t('purchase_orders.receipt.reference_placeholder', 'DN-1234...')}
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>{t('purchase_orders.receipt.notes', 'Notes')}</Label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" />
                    </div>
                    <div className="rounded-lg border">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-muted/50 border-b">
                                    <th className="px-3 py-2 text-left font-medium">{t('purchase_orders.receipt.item', 'Item')}</th>
                                    <th className="px-3 py-2 text-right font-medium">{t('purchase_orders.receipt.qty_ordered', 'Ordered')}</th>
                                    <th className="px-3 py-2 text-right font-medium">
                                        {t('purchase_orders.receipt.already_received', 'Already Received')}
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                        {t('purchase_orders.receipt.qty_this_delivery', 'Qty This Delivery')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {openItems.map((item) => (
                                    <tr key={item.id} className="border-b last:border-0">
                                        <td className="px-3 py-2">{item.description}</td>
                                        <td className="text-muted-foreground px-3 py-2 text-right">
                                            {Number(item.qty_ordered)} {item.unit}
                                        </td>
                                        <td className="text-muted-foreground px-3 py-2 text-right">
                                            {Number(item.qty_received ?? 0).toFixed(item.unit === 'pcs' ? 0 : 2)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <Input
                                                type="number"
                                                step="any"
                                                min="0"
                                                max={Number(item.qty_ordered) - Number(item.qty_received ?? 0)}
                                                value={qtys[item.id] ?? ''}
                                                onChange={(e) => setQtys((prev) => ({ ...prev, [item.id]: e.target.value }))}
                                                className="ml-auto h-7 w-24 text-right"
                                                placeholder="0"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </ResponsiveModalBody>
                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={onClose}>
                        {t('purchase_orders.receipt.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Saving...' : t('purchase_orders.receipt.submit', 'Save Receipt')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}

// ─── Link sheet ───────────────────────────────────────────────────────────────

type PurposeType = 'warehouse' | 'project' | 'lead';

interface LinkDialogProps {
    open: boolean;
    onClose: () => void;
    po: PurchaseOrder;
    projects: { id: string; uuid: string; contract_name: string; contract_number: string }[];
    leads: { id: string; uuid: string; lead_number: string | null }[];
    t: (key: string, fallback?: string) => string;
}

function LinkDialog({ open, onClose, po, projects, leads, t }: LinkDialogProps) {
    const initialPurpose: PurposeType = po.project_id ? 'project' : po.lead_id ? 'lead' : 'warehouse';
    const [purpose, setPurpose] = useState<PurposeType>(initialPurpose);
    const [projectId, setProjectId] = useState(po.project_id ?? '');
    const [leadId, setLeadId] = useState(po.lead_id ?? '');
    const [saving, setSaving] = useState(false);

    const projectOptions = projects.map((p) => ({ value: p.uuid, label: `${p.contract_name} (${p.contract_number})` }));
    const leadOptions = leads.map((l) => ({ value: l.uuid, label: l.lead_number ?? l.uuid }));

    const handleSave = () => {
        setSaving(true);
        router.patch(
            route('purchase-orders.link', po.id),
            {
                project_id: purpose === 'project' ? projectId || null : null,
                lead_id: purpose === 'lead' ? leadId || null : null,
            } as never,
            {
                preserveScroll: true,
                onSuccess: () => {
                    onClose();
                    toast.success(t('purchase_orders.messages.linked', 'Purchase order linked.'));
                },
                onError: () => toast.error(t('toasts.purchase_orders_show.link_failed', 'Failed to update link.')),
                onFinish: () => setSaving(false),
            },
        );
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onClose} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('purchase_orders.show.link_dialog.title', 'Link to Project / Lead')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('purchase_orders.show.link_dialog.desc', 'Connect this purchase order to a project, lead, or warehouse stock.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>
                <ResponsiveModalBody className="space-y-4">
                    <div className="flex gap-2">
                        {(['warehouse', 'project', 'lead'] as PurposeType[]).map((p) => (
                            <button
                                key={p}
                                type="button"
                                onClick={() => setPurpose(p)}
                                className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${purpose === p ? 'border-primary/40 bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
                            >
                                {p === 'warehouse' && t('purchase_orders.form.purpose_warehouse', 'Warehouse / Stock')}
                                {p === 'project' && t('purchase_orders.form.purpose_project', 'Project')}
                                {p === 'lead' && t('purchase_orders.form.purpose_lead', 'Lead')}
                            </button>
                        ))}
                    </div>
                    {purpose === 'project' && (
                        <Combobox
                            options={projectOptions}
                            value={projectId}
                            onValueChange={setProjectId}
                            placeholder={t('purchase_orders.form.select_project', 'Search projects...')}
                            searchPlaceholder="Type to search..."
                            emptyText="No projects found."
                            allowClear
                            clearText="No project"
                            requireSearchQuery={projectOptions.length > 10}
                            typeToSearchHint="Type to search projects..."
                        />
                    )}
                    {purpose === 'lead' && (
                        <Combobox
                            options={leadOptions}
                            value={leadId}
                            onValueChange={setLeadId}
                            placeholder={t('purchase_orders.form.select_lead', 'Search leads...')}
                            searchPlaceholder="Type to search..."
                            emptyText="No leads found."
                            allowClear
                            clearText="No lead"
                            requireSearchQuery={leadOptions.length > 10}
                            typeToSearchHint="Type to search leads..."
                        />
                    )}
                </ResponsiveModalBody>
                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={onClose}>
                        {t('purchase_orders.form.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : t('common.save', 'Save')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}

// ─── Edit Details sheet ────────────────────────────────────────────────────────

interface EditDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    po: PurchaseOrder;
    t: (key: string, fallback?: string) => string;
}

function EditDetailsDialog({ open, onClose, po, t }: EditDetailsDialogProps) {
    const [orderDate, setOrderDate] = useState(po.order_date ?? '');
    const [expectedDelivery, setExpectedDelivery] = useState(po.expected_delivery_date ?? '');
    const [notes, setNotes] = useState(po.notes ?? '');
    const [directoNumber, setDirectoNumber] = useState(po.directo_number ?? '');
    const [saving, setSaving] = useState(false);

    const handleSave = () => {
        setSaving(true);
        router.patch(
            route('purchase-orders.update-details', po.id),
            {
                order_date: orderDate,
                expected_delivery_date: expectedDelivery || null,
                notes: notes || null,
                directo_number: directoNumber || null,
            } as never,
            {
                preserveScroll: true,
                onSuccess: () => {
                    onClose();
                    toast.success(t('purchase_orders.messages.details_updated', 'Details updated.'));
                },
                onError: () => toast.error(t('toasts.purchase_orders_show.details_failed', 'Failed to update details.')),
                onFinish: () => setSaving(false),
            },
        );
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onClose} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('purchase_orders.show.edit_details_dialog.title', 'Edit Details')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('purchase_orders.show.edit_details_dialog.desc', 'Update dates, notes, and internal reference numbers.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>
                <ResponsiveModalBody className="space-y-4">
                    <div>
                        <Label className="mb-1.5 block text-sm font-medium">
                            {t('purchase_orders.form.order_date', 'Order Date')} <span className="text-destructive">*</span>
                        </Label>
                        <DatePicker value={orderDate} onValueChange={setOrderDate} placeholder="Pick a date" />
                    </div>
                    <div>
                        <Label className="mb-1.5 block text-sm font-medium">{t('purchase_orders.form.expected_delivery', 'Expected Delivery')}</Label>
                        <DatePicker value={expectedDelivery} onValueChange={setExpectedDelivery} placeholder="Pick a date" />
                    </div>
                    {po.status !== 'draft' && (
                        <div>
                            <Label className="mb-1.5 block text-sm font-medium">{t('purchase_orders.show.directo_number', 'Directo Nr')}</Label>
                            <Input value={directoNumber} onChange={(e) => setDirectoNumber(e.target.value)} placeholder="e.g. DIR-12345" />
                        </div>
                    )}
                    <div>
                        <Label className="mb-1.5 block text-sm font-medium">{t('purchase_orders.form.sections.notes', 'Notes')}</Label>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
                    </div>
                </ResponsiveModalBody>
                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={onClose}>
                        {t('purchase_orders.form.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving || !orderDate}>
                        {saving ? 'Saving...' : t('common.save', 'Save')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PurchaseOrderShow() {
    const { props } = usePage<POShowProps>();
    const { purchaseOrder: po, isOverdue, categories, projects, leads, auth } = props;
    const { t } = useTranslations();
    const isAdmin = Array.isArray((auth as { roles?: string[] })?.roles) && (auth as { roles?: string[] }).roles!.includes('Administrator');

    const [receiptOpen, setReceiptOpen] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [editDetailsOpen, setEditDetailsOpen] = useState(false);
    const [lineItems, setLineItems] = useState<EditablePurchaseOrderItemForm[]>(() =>
        po.status === 'draft' ? ensureTrailingRow(itemsFromPO(po.items)) : itemsFromPO(po.items),
    );
    const [savingLineItems, setSavingLineItems] = useState(false);

    const breadcrumbs: BreadcrumbItem[] = [
        { title: t('dashboard.title', 'Dashboard'), href: '/' },
        { title: t('purchase_orders.index.title', 'Purchase Orders'), href: route('purchase-orders.index') },
        { title: po.po_number, href: '#' },
    ];

    const handleDuplicate = () => {
        router.post(
            route('purchase-orders.duplicate', po.id),
            {},
            {
                onSuccess: () => toast.success(t('purchase_orders.messages.duplicated', 'Purchase order duplicated.')),
                onError: () => toast.error(t('toasts.purchase_orders_show.duplicate_failed', 'Failed to duplicate.')),
            },
        );
    };

    const handleCancel = () => {
        if (!confirm('Cancel this purchase order?')) return;
        router.patch(
            route('purchase-orders.update-status', po.id),
            { status: 'cancelled' },
            {
                onSuccess: () => toast.success(t('toasts.purchase_orders_show.cancelled', 'Purchase order cancelled.')),
            },
        );
    };

    const handleMarkOrdered = () => {
        router.patch(
            route('purchase-orders.update-status', po.id),
            { status: 'ordered' },
            {
                onSuccess: () => toast.success(t('purchase_orders.messages.status_updated', 'Status updated.')),
            },
        );
    };

    const handleItemStatusChange = (itemId: string, status: string) => {
        router.patch(
            route('purchase-orders.items.update-status', { purchaseOrder: po.id, item: itemId }),
            { item_status: status || null },
            { preserveScroll: true },
        );
    };

    const handleAdminStatusChange = (status: string) => {
        router.patch(
            route('purchase-orders.update-status-admin', po.id),
            { status },
            {
                preserveScroll: true,
                onSuccess: () => toast.success(t('purchase_orders.messages.status_updated', 'Status updated.')),
                onError: () => toast.error(t('toasts.purchase_orders_show.status_failed', 'Failed to update status.')),
            },
        );
    };

    const handleDelete = () => {
        if (!confirm('Delete this purchase order? This cannot be undone.')) return;
        router.delete(route('purchase-orders.destroy', po.id), {
            onSuccess: () => toast.success(t('purchase_orders.messages.deleted', 'Purchase order deleted.')),
        });
    };

    const canEdit = po.status === 'draft';
    const canRecordReceipt = ['ordered', 'partially_received'].includes(po.status);
    const canDelete = po.status === 'draft';
    const lineItemsSubtotal = calcSubtotal(lineItems);
    const hasMissingItems = po.items.some((item) => Number(item.qty_received ?? 0) < Number(item.qty_ordered));
    const showMissingItemsWarning = isOverdue && hasMissingItems && po.status !== 'draft' && po.status !== 'cancelled';

    const isFirstRender = useRef(true);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (!canEdit) return;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        setSavingLineItems(true);

        saveTimerRef.current = setTimeout(() => {
            const validItems = serializeItems(lineItems);
            router.put(
                route('purchase-orders.update', po.id),
                {
                    supplier_company_id: po.supplier_company_id,
                    lead_id: po.lead_id,
                    project_id: po.project_id,
                    currency: po.currency,
                    order_date: po.order_date,
                    expected_delivery_date: po.expected_delivery_date,
                    notes: po.notes,
                    items: validItems,
                } as never,
                {
                    preserveScroll: true,
                    onError: () => toast.error(t('toasts.purchase_orders_show.line_items_failed', 'Failed to save line items.')),
                    onFinish: () => setSavingLineItems(false),
                },
            );
        }, 1200);

        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, [lineItems]); // eslint-disable-line react-hooks/exhaustive-deps

    const statusFlowDisplay = ['draft', 'ordered', 'partially_received', 'received'] as PurchaseOrderStatus[];
    const currentStatusIdx = statusFlowDisplay.indexOf(po.status as PurchaseOrderStatus);
    const creatorName = po.creator ? `${po.creator.first_name} ${po.creator.last_name}` : '—';
    const isTabletOrMobile = useIsTabletOrMobile();
    const [activeTab, setActiveTab] = useState('details');
    const [mobileActionsDrawerOpen, setMobileActionsDrawerOpen] = useState(false);

    /**
     * Closes the actions drawer, then runs the opener once it has animated out.
     * The delay is not cosmetic: mounting the next overlay while this one is
     * still leaving stacks two of them and leaves the body with a stuck
     * pointer-events lock. It cannot hang off the drawer's onOpenChange — vaul
     * only fires that for closes it initiates itself.
     */
    const requestActionFromDrawer = (open: () => void) => {
        setMobileActionsDrawerOpen(false);

        window.setTimeout(open, 350);
    };
    const convertedProject = po.project?.lead_id === po.lead_id ? po.project : po.lead?.project;
    const hasConvertedLeadProject = Boolean(po.lead && convertedProject?.lead_id === po.lead_id);
    const orderPurpose = hasConvertedLeadProject
        ? {
              label: t('purchase_orders.show.order_for_converted_project', 'Lead / Project'),
              icon: <FolderOpen className="text-muted-foreground h-4 w-4" />,
              title: convertedProject?.contract_name ?? '—',
              subtitle: po.lead?.lead_number,
              project: convertedProject,
              lead: po.lead,
          }
        : po.project
          ? {
                label: t('purchase_orders.show.project', 'Project'),
                icon: <FolderOpen className="text-muted-foreground h-4 w-4" />,
                title: po.project.contract_name,
                subtitle: po.project.contract_number,
                project: po.project,
                lead: null,
            }
          : po.lead
            ? {
                  label: t('purchase_orders.show.lead', 'Lead'),
                  icon: <Handshake className="text-muted-foreground h-4 w-4" />,
                  title: po.lead.lead_number ?? '—',
                  subtitle: null,
                  project: null,
                  lead: po.lead,
              }
            : {
                  label: t('purchase_orders.show.warehouse', 'Warehouse'),
                  icon: <Warehouse className="text-muted-foreground h-4 w-4" />,
                  title: t('purchase_orders.show.warehouse_stock', 'Warehouse / Stock'),
                  subtitle: t('purchase_orders.show.no_project_or_lead', 'No project or lead attached'),
                  project: null,
                  lead: null,
              };

    const tabs = useMemo(
        () => [
            { value: 'details', label: t('purchase_orders.show.tabs.details', 'Details'), icon: <Info className="h-4 w-4" /> },
            {
                value: 'receipt_history',
                label: t('purchase_orders.show.tabs.receipt_history', 'Receipt History'),
                icon: <Receipt className="h-4 w-4" />,
            },
            { value: 'notes', label: t('purchase_orders.show.tabs.notes', 'Notes & Attachments'), icon: <MessageSquare className="h-4 w-4" /> },
            { value: 'activity', label: t('purchase_orders.show.tabs.activity', 'Activity'), icon: <Activity className="h-4 w-4" /> },
        ],
        [t],
    );

    const actionButtons = (
        <>
            {po.status === 'draft' && (
                <Button variant="outline" size="sm" className="gap-2 whitespace-nowrap" onClick={handleMarkOrdered}>
                    <CheckCircle2 className="h-4 w-4" />
                    {t('purchase_orders.show.actions.mark_as_ordered', 'Mark as Ordered')}
                </Button>
            )}
            {canRecordReceipt && (
                <Button size="sm" className="gap-2 whitespace-nowrap" onClick={() => setReceiptOpen(true)}>
                    <ClipboardCheck className="h-4 w-4" />
                    {t('purchase_orders.show.record_receipt', 'Record Receipt')}
                </Button>
            )}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1.5 whitespace-nowrap">
                        <MoreHorizontal className="h-4 w-4" />
                        {t('purchase_orders.show.actions.more', 'More')}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => setLinkOpen(true)}>
                        <Link2 className="h-4 w-4" />
                        {t('purchase_orders.show.actions.link', 'Link to Project / Lead')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDuplicate}>
                        <Copy className="h-4 w-4" />
                        {t('purchase_orders.show.actions.duplicate', 'Duplicate PO')}
                    </DropdownMenuItem>
                    <a href={route('purchase-orders.export-excel', po.id)}>
                        <DropdownMenuItem>
                            <FileSpreadsheet className="text-cat-teal-foreground h-4 w-4" />
                            {t('purchase_orders.show.export_excel', 'Export to Excel')}
                        </DropdownMenuItem>
                    </a>
                    {isAdmin && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                    <Circle className="h-4 w-4" />
                                    {t('purchase_orders.show.actions.change_status', 'Change Status')}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                    <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                                        {t('purchase_orders.show.actions.admin_override', 'Admin override')}
                                    </DropdownMenuLabel>
                                    {(['draft', 'ordered', 'partially_received', 'received', 'cancelled'] as const).map((status) => (
                                        <DropdownMenuItem
                                            key={status}
                                            onClick={() => handleAdminStatusChange(status)}
                                            className={po.status === status ? 'font-medium' : ''}
                                            disabled={po.status === status}
                                        >
                                            {po.status === status && <CheckCircle2 className="text-success h-4 w-4" />}
                                            {t(`purchase_orders.statuses.${status}`, status.replace(/_/g, ' '))}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuSubContent>
                            </DropdownMenuSub>
                        </>
                    )}
                    {(po.status !== 'cancelled' && po.status !== 'received') || canDelete ? <DropdownMenuSeparator /> : null}
                    {po.status !== 'cancelled' && po.status !== 'received' && (
                        <DropdownMenuItem onClick={handleCancel} className="text-destructive focus:text-destructive">
                            <XCircle className="h-4 w-4" />
                            {t('purchase_orders.show.actions.cancel', 'Cancel PO')}
                        </DropdownMenuItem>
                    )}
                    {canDelete && (
                        <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                            <Trash2 className="h-4 w-4" />
                            {t('purchase_orders.show.actions.delete', 'Delete PO')}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0 rounded-full"
                title={t('purchase_orders.show.actions.edit_details', 'Edit Details')}
                onClick={() => setEditDetailsOpen(true)}
            >
                <Pencil className="h-4 w-4" />
            </Button>
        </>
    );

    const mobileActions = (
        <>
            <Button
                type="button"
                variant="ghost"
                className="h-11 w-full justify-start gap-2"
                onClick={() => requestActionFromDrawer(() => setEditDetailsOpen(true))}
            >
                <Pencil className="h-4 w-4" />
                {t('purchase_orders.show.actions.edit_details', 'Edit Details')}
            </Button>
            <Button
                type="button"
                variant="ghost"
                className="h-11 w-full justify-start gap-2"
                onClick={() => requestActionFromDrawer(() => setLinkOpen(true))}
            >
                <Link2 className="h-4 w-4" />
                {t('purchase_orders.show.actions.link', 'Link to Project / Lead')}
            </Button>
            <Button type="button" variant="ghost" className="h-11 w-full justify-start gap-2" onClick={handleDuplicate}>
                <Copy className="h-4 w-4" />
                {t('purchase_orders.show.actions.duplicate', 'Duplicate PO')}
            </Button>
            {po.status === 'draft' && (
                <Button type="button" variant="ghost" className="h-11 w-full justify-start gap-2" onClick={handleMarkOrdered}>
                    <CheckCircle2 className="h-4 w-4" />
                    {t('purchase_orders.show.actions.mark_as_ordered', 'Mark as Ordered')}
                </Button>
            )}
            <a href={route('purchase-orders.export-excel', po.id)} className="block">
                <Button type="button" variant="ghost" className="h-11 w-full justify-start gap-2">
                    <FileSpreadsheet className="text-cat-teal-foreground h-4 w-4" />
                    {t('purchase_orders.show.export_excel', 'Export to Excel')}
                </Button>
            </a>
            {canRecordReceipt && (
                <Button
                    type="button"
                    variant="ghost"
                    className="h-11 w-full justify-start gap-2"
                    onClick={() => requestActionFromDrawer(() => setReceiptOpen(true))}
                >
                    <ClipboardCheck className="h-4 w-4" />
                    {t('purchase_orders.show.record_receipt', 'Record Receipt')}
                </Button>
            )}
            {po.status !== 'cancelled' && po.status !== 'received' && (
                <Button type="button" variant="ghost" className="text-destructive h-11 w-full justify-start gap-2" onClick={handleCancel}>
                    <XCircle className="h-4 w-4" />
                    {t('purchase_orders.show.actions.cancel', 'Cancel PO')}
                </Button>
            )}
            {canDelete && (
                <Button type="button" variant="ghost" className="text-destructive h-11 w-full justify-start gap-2" onClick={handleDelete}>
                    <Trash2 className="h-4 w-4" />
                    {t('purchase_orders.show.actions.delete', 'Delete PO')}
                </Button>
            )}
        </>
    );

    return (
        <>
            <Head title={po.po_number} />
            <ShowLayout
                breadcrumbs={breadcrumbs}
                title={
                    <>
                        {po.po_number}
                        <span className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm font-normal">
                            <span>
                                {t('purchase_orders.show.created_on', 'Created on')} {safeFormatDate(po.created_at)}
                                {creatorName ? ` · ${creatorName}` : ''}
                            </span>
                            <span>
                                {t('purchase_orders.show.order_date_label', 'Order date')}: {safeFormatDate(po.order_date)}
                            </span>
                            {po.expected_delivery_date && (
                                <span>
                                    {t('purchase_orders.show.delivery_label', 'Delivery')}: {safeFormatDate(po.expected_delivery_date)}
                                </span>
                            )}
                        </span>
                    </>
                }
            >
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex min-w-0 items-center gap-3">
                        <Link href={route('purchase-orders.index')}>
                            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 rounded-lg">
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        </Link>
                        <StatusBadge status={po.status} />
                        {isOverdue && (
                            <span className="text-destructive inline-flex items-center gap-1 text-sm font-medium">
                                <AlertCircle className="h-4 w-4" />
                                {t('purchase_orders.index.overdue', 'Overdue')}
                            </span>
                        )}
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 w-full flex-1 flex-col">
                        <div className="mb-2 flex min-w-0 items-center justify-between gap-3">
                            {isTabletOrMobile ? (
                                <div className="-mx-1 overflow-x-auto pb-1">
                                    <div className="flex w-max min-w-full items-stretch gap-2 px-1">
                                        {tabs.map((tab) => {
                                            const isActive = activeTab === tab.value;

                                            return (
                                                <button
                                                    key={tab.value}
                                                    type="button"
                                                    onClick={() => setActiveTab(tab.value)}
                                                    className={cn(
                                                        'h-auto min-w-[120px] rounded-xl border px-4 py-3 text-left transition-colors',
                                                        isActive
                                                            ? 'border-primary/40 bg-primary/10 text-primary'
                                                            : 'border-border bg-background text-foreground',
                                                    )}
                                                >
                                                    <span className="flex flex-col items-start gap-1.5">
                                                        <span className="text-muted-foreground">{tab.icon}</span>
                                                        <span className="text-sm leading-tight font-medium">{tab.label}</span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <TabsList className="bg-muted flex h-auto min-w-0 flex-none flex-nowrap items-center gap-1 rounded-xl px-1 py-1">
                                    {tabs.map((tab) => (
                                        <TabsTrigger key={tab.value} value={tab.value} className="rounded-md text-sm" title={tab.label}>
                                            <span className="inline-flex items-center gap-2">
                                                {tab.icon}
                                                <span className="hidden xl:inline">{tab.label}</span>
                                            </span>
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            )}

                            <div className="hidden flex-none items-center gap-2 lg:flex">{actionButtons}</div>
                        </div>

                        <TabsContent value="details" className="mt-0 space-y-5 outline-none data-[state=inactive]:hidden">
                            {showMissingItemsWarning && (
                                <div className="border-border bg-warning-subtle text-warning-subtle-foreground flex items-start gap-3 rounded-lg border px-4 py-3 text-sm">
                                    <AlertCircle className="text-warning-subtle-foreground mt-0.5 h-4 w-4 shrink-0" />
                                    <div>
                                        <p className="font-medium">
                                            {t('purchase_orders.show.warnings.overdue_missing_title', 'Delivery overdue — items missing')}
                                        </p>
                                        <p className="text-warning-subtle-foreground mt-0.5 text-xs">
                                            {t(
                                                'purchase_orders.show.warnings.overdue_missing_desc',
                                                'The expected delivery date has passed but not all items have been received. Record a receipt or update the delivery date.',
                                            )}
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
                                <div className="grid grid-cols-1 overflow-hidden rounded-xl border md:grid-cols-2 xl:grid-cols-4">
                                    <div className="space-y-2 border-b p-4 md:border-r xl:border-b">
                                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                            {t('purchase_orders.show.supplier', 'Supplier')}
                                        </p>
                                        <div className="flex items-center gap-2 font-semibold">
                                            <Building2 className="text-muted-foreground h-4 w-4" />
                                            {po.supplier?.name ?? '—'}
                                        </div>
                                        {po.supplier && (
                                            <Link href={`/companies/${po.supplier_company_id}`} className="text-primary ml-6 text-sm hover:underline">
                                                {t('purchase_orders.show.view_company', 'View company')}
                                            </Link>
                                        )}
                                    </div>
                                    <div className="space-y-2 border-b p-4 md:border-r xl:col-span-2 xl:border-b">
                                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{orderPurpose.label}</p>
                                        <div className="flex items-center gap-2 font-semibold">
                                            {orderPurpose.icon}
                                            {orderPurpose.title}
                                        </div>
                                        {orderPurpose.subtitle && <p className="text-muted-foreground ml-6 text-sm">{orderPurpose.subtitle}</p>}
                                        <div className="ml-6 flex flex-wrap gap-3 text-sm">
                                            {orderPurpose.project && (
                                                <Link href={`/projects/${orderPurpose.project.id}`} className="text-primary hover:underline">
                                                    {t('purchase_orders.show.view_project', 'View project')}
                                                </Link>
                                            )}
                                            {orderPurpose.lead && (
                                                <Link href={`/leads/${orderPurpose.lead.id}`} className="text-primary hover:underline">
                                                    {t('purchase_orders.show.view_lead', 'View lead')}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-2 border-b p-4 xl:border-r xl:border-b">
                                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                            {t('purchase_orders.show.order_date', 'Order Date')}
                                        </p>
                                        <div className="flex items-center gap-2 text-sm font-medium">
                                            <CalendarDays className="text-muted-foreground h-4 w-4" />
                                            {safeFormatDate(po.order_date)}
                                        </div>
                                    </div>
                                    <div className="space-y-2 border-b p-4 md:border-r">
                                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                            {t('purchase_orders.show.expected_delivery', 'Expected Delivery')}
                                        </p>
                                        <div className={`flex items-center gap-2 text-sm font-medium ${isOverdue ? 'text-destructive' : ''}`}>
                                            <CalendarDays className="h-4 w-4" />
                                            {safeFormatDate(po.expected_delivery_date)}
                                        </div>
                                        {isOverdue && (
                                            <p className="text-destructive ml-6 text-xs font-medium">
                                                {t('purchase_orders.index.overdue', 'Overdue')}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2 border-b p-4 md:border-b-0 xl:border-r">
                                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                            {t('purchase_orders.show.currency', 'Currency')}
                                        </p>
                                        <span className="bg-muted inline-flex rounded-md px-2.5 py-1 text-xs font-semibold">{po.currency}</span>
                                    </div>
                                    {po.directo_number && (
                                        <div className="space-y-2 border-b p-4 md:border-b-0 xl:border-r">
                                            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                                {t('purchase_orders.show.directo_number', 'Directo Nr')}
                                            </p>
                                            <span className="bg-primary/10 text-primary inline-flex rounded-md px-2.5 py-1 text-xs font-semibold">
                                                {po.directo_number}
                                            </span>
                                        </div>
                                    )}
                                    <div className="space-y-2 p-4 md:col-span-1 xl:col-span-2">
                                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                            {t('purchase_orders.show.notes', 'Notes')}
                                        </p>
                                        <p className="text-sm">{po.notes || t('purchase_orders.show.no_notes', 'No notes added.')}</p>
                                    </div>
                                </div>

                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-sm font-semibold">
                                            {t('purchase_orders.show.status_progress.title', 'Status Progress')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-0">
                                        {statusFlowDisplay.map((status, idx) => {
                                            const isCurrent = po.status === status;
                                            const isPast = currentStatusIdx > idx;
                                            const isLast = idx === statusFlowDisplay.length - 1;

                                            return (
                                                <div key={status} className="grid grid-cols-[24px_1fr] gap-3">
                                                    <div className="flex flex-col items-center">
                                                        {isPast ? (
                                                            <CheckCircle2 className="text-success h-5 w-5" />
                                                        ) : isCurrent ? (
                                                            <Circle className="fill-warning-subtle text-warning h-5 w-5" />
                                                        ) : (
                                                            <Circle className="text-muted-foreground/40 h-5 w-5" />
                                                        )}
                                                        {!isLast && <div className={`mt-1 h-9 w-px ${isPast ? 'bg-success' : 'bg-border'}`} />}
                                                    </div>
                                                    <div className="pb-4">
                                                        <p
                                                            className={`text-sm font-semibold ${
                                                                isCurrent ? 'text-foreground' : isPast ? 'text-foreground' : 'text-muted-foreground'
                                                            }`}
                                                        >
                                                            {t(`purchase_orders.show.status_progress.${status}`, status.replace(/_/g, ' '))}
                                                        </p>
                                                        {isCurrent ? (
                                                            <p className="text-warning-subtle-foreground mt-1 text-xs font-medium">
                                                                {t('purchase_orders.show.status_progress.current_status', 'Current status')}
                                                            </p>
                                                        ) : isPast ? (
                                                            <p className="text-muted-foreground mt-1 text-xs">{safeFormatDate(po.created_at)}</p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {po.status === 'cancelled' && (
                                            <div className="grid grid-cols-[24px_1fr] gap-3">
                                                <XCircle className="text-destructive h-5 w-5" />
                                                <div>
                                                    <p className="text-destructive text-sm font-semibold">
                                                        {t('purchase_orders.show.status_progress.cancelled', 'Cancelled')}
                                                    </p>
                                                    <p className="text-muted-foreground mt-1 text-xs">
                                                        {t('purchase_orders.show.status_progress.current_status', 'Current status')}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>

                            <Card>
                                <CardHeader className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="space-y-1">
                                        <CardTitle className="text-base">{t('purchase_orders.show.line_items.title', 'Line Items')}</CardTitle>
                                        <p className="text-muted-foreground text-sm">
                                            {canEdit
                                                ? t(
                                                      'purchase_orders.show.line_items.draft_edit_hint',
                                                      'Draft purchase orders use the same editable grid as creation. Tab, Enter, or paste from Excel to work quickly.',
                                                  )
                                                : t(
                                                      'purchase_orders.show.line_items.locked_hint',
                                                      'Line items are locked after the purchase order is ordered. Justification and notes remain visible for review.',
                                                  )}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button variant="outline" size="sm" className="gap-2" disabled>
                                            <FileSpreadsheet className="h-4 w-4" />
                                            {t('purchase_orders.show.line_items.import_excel', 'Import from Excel')}
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <ItemGrid
                                        items={lineItems}
                                        onChange={setLineItems}
                                        readOnly={!canEdit}
                                        currency={po.currency}
                                        categories={categories}
                                        canRemoveRows={canDelete}
                                        showStatus={true}
                                        showQtyReceived={!canEdit}
                                        showAddRowAction={false}
                                        onStatusChange={handleItemStatusChange}
                                        t={t}
                                    />
                                    <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="text-sm font-semibold">
                                            {t('purchase_orders.show.line_items.total', 'Total')} ({po.currency}){' '}
                                            <span className="tabular-nums">{formatCurrency(lineItemsSubtotal, po.currency)}</span>
                                        </div>
                                        {canEdit && savingLineItems && (
                                            <span className="text-muted-foreground text-xs">{t('common.saving', 'Saving...')}</span>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">{t('purchase_orders.show.order_summary.title', 'Order Summary')}</CardTitle>
                                </CardHeader>
                                <CardContent className="grid gap-3 text-sm md:grid-cols-2">
                                    <div className="flex justify-between rounded-lg border px-3 py-2">
                                        <span className="text-muted-foreground">{t('purchase_orders.show.order_summary.subtotal', 'Subtotal')}</span>
                                        <span className="font-medium">{formatCurrency(po.subtotal, po.currency)}</span>
                                    </div>
                                    <div className="flex justify-between rounded-lg border px-3 py-2 text-base font-bold">
                                        <span>
                                            {t('purchase_orders.show.order_summary.total', 'Total')} ({po.currency})
                                        </span>
                                        <span>{formatCurrency(po.total, po.currency)}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="receipt_history" className="mt-0">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-base">{t('purchase_orders.show.tabs.receipt_history', 'Receipt History')}</CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    {po.receipts.length === 0 ? (
                                        <p className="text-muted-foreground px-4 py-8 text-center text-sm">
                                            {t('purchase_orders.show.receipt_history.no_receipts', 'No receipts recorded yet.')}
                                        </p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="bg-muted/40 text-muted-foreground border-y text-xs font-medium">
                                                        <th className="px-4 py-3 text-left">#</th>
                                                        <th className="px-4 py-3 text-left">
                                                            {t('purchase_orders.show.receipt_history.columns.date', 'Date')}
                                                        </th>
                                                        <th className="px-4 py-3 text-left">
                                                            {t('purchase_orders.show.receipt_history.columns.received_by', 'Received By')}
                                                        </th>
                                                        <th className="px-4 py-3 text-left">
                                                            {t('purchase_orders.show.receipt_history.columns.reference', 'Reference')}
                                                        </th>
                                                        <th className="px-4 py-3 text-right">
                                                            {t('purchase_orders.show.receipt_history.columns.lines_affected', 'Lines')}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {po.receipts.map((receipt, idx) => (
                                                        <tr key={receipt.id} className="border-b last:border-0">
                                                            <td className="text-muted-foreground px-4 py-3">{idx + 1}</td>
                                                            <td className="px-4 py-3">{safeFormatDateTime(receipt.received_at)}</td>
                                                            <td className="px-4 py-3">
                                                                {receipt.receiver
                                                                    ? `${receipt.receiver.first_name} ${receipt.receiver.last_name}`
                                                                    : '—'}
                                                            </td>
                                                            <td className="text-muted-foreground px-4 py-3">
                                                                {receipt.reference ?? receipt.notes ?? '—'}
                                                            </td>
                                                            <td className="px-4 py-3 text-right">{receipt.receipt_items?.length ?? 0}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="notes" className="mt-0">
                            <Card>
                                <CardContent className="text-muted-foreground py-10 text-center text-sm">
                                    {po.notes || t('purchase_orders.show.notes_empty', 'No notes or attachments yet.')}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="activity" className="mt-0">
                            <Card>
                                <CardContent className="text-muted-foreground py-10 text-center text-sm">
                                    {t('purchase_orders.show.activity_empty', 'Activity log will appear here.')}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </ShowLayout>

            {isTabletOrMobile && (
                <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 safe-area-inset-bottom fixed right-0 bottom-0 left-0 z-50 border-t p-4 shadow-lg backdrop-blur lg:hidden">
                    <div className="grid grid-cols-3 gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setMobileActionsDrawerOpen(true)}
                            aria-label={t('common.more_actions', 'More actions')}
                        >
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                        <a href={route('purchase-orders.export-excel', po.id)} className="block">
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                aria-label={t('purchase_orders.show.export_excel', 'Export to Excel')}
                            >
                                <FileSpreadsheet className="text-cat-teal-foreground h-4 w-4" />
                            </Button>
                        </a>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setReceiptOpen(true)}
                            disabled={!canRecordReceipt}
                            aria-label={t('purchase_orders.show.record_receipt', 'Record Receipt')}
                        >
                            <ClipboardCheck className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            <Drawer open={mobileActionsDrawerOpen} onOpenChange={setMobileActionsDrawerOpen} direction="bottom">
                <DrawerContent className="lg:hidden">
                    <DrawerHeader>
                        <DrawerTitle>{t('companies.sidebar.actions', 'Actions')}</DrawerTitle>
                        <DrawerDescription>{t('common.select_action', 'Select an action')}</DrawerDescription>
                    </DrawerHeader>
                    <div className="space-y-1 p-4 pt-0">{mobileActions}</div>
                </DrawerContent>
            </Drawer>
            {isTabletOrMobile && <div className="h-24 lg:h-0" />}

            <ReceiptDialog open={receiptOpen} onClose={() => setReceiptOpen(false)} po={po} t={t} />

            <LinkDialog open={linkOpen} onClose={() => setLinkOpen(false)} po={po} projects={projects} leads={leads} t={t} />

            <EditDetailsDialog open={editDetailsOpen} onClose={() => setEditDetailsOpen(false)} po={po} t={t} />
        </>
    );
}
